import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { ChatWindow } from '../chat/ChatWindow';
import { ChatList } from '../chat/chat-list/ChatList';
import { Newsfeed } from '../chat/news/Newsfeed';
import { useSettings } from '../../libs/hooks/useSettings';

const MIN_NEWSFEED_WIDTH = 240;
const MAX_NEWSFEED_WIDTH = 800;
const DEFAULT_NEWSFEED_WIDTH = 600;
const clamp = (v: number) => Math.min(MAX_NEWSFEED_WIDTH, Math.max(MIN_NEWSFEED_WIDTH, v));

const useClasses = makeStyles({
    container: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'start',
        height: '100%',
        width: '100%'
    },
    chatWindowWrapper: {
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
    },
    resizer: {
        width: '4px',
        cursor: 'col-resize',
        backgroundColor: tokens.colorNeutralStroke1,
        transition: 'background-color .15s ease',
        '&:hover': { backgroundColor: tokens.colorNeutralStroke2 }
    },
    resizerActive: { backgroundColor: tokens.colorNeutralStroke3 }
});

export const ChatView: FC = () => {
    const classes = useClasses();
    const { selectedId } = useAppSelector((state: RootState) => state.conversations);
    const settings = useSettings();

    const [isNewsDrawerOpen, setIsNewsDrawerOpen] = useState(false);
    const [unreadNewsCount, setUnreadNewsCount] = useState<number>(0);
    const [newsfeedHideable, setNewsfeedHideable] = useState<boolean>(true);
    const [newsfeedWidth, setNewsfeedWidth] = useState<number>(DEFAULT_NEWSFEED_WIDTH);

    const isResizingRef = useRef(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    useEffect(() => {
        settings.load()
            .then(settingsData => {
                setNewsfeedHideable(settingsData.newsfeedHideable);
                if (!settingsData.newsfeedHideable) setIsNewsDrawerOpen(true);
            })
            .catch(() => { setNewsfeedHideable(true); });
    }, [settings]);

    const toggleNewsDrawer = () => {
        if (newsfeedHideable) setIsNewsDrawerOpen(o => !o);
    };

    const finishResizing = () => {
        if (!isResizingRef.current) return;
        isResizingRef.current = false;
        document.body.style.userSelect = '';
    };

    const startResizing = (e: React.PointerEvent) => {
        if (!isNewsDrawerOpen) return;
        isResizingRef.current = true;
        startXRef.current = e.clientX;
        startWidthRef.current = newsfeedWidth;
        document.body.style.userSelect = 'none';

        const handleMove = (ev: PointerEvent) => {
            if (!isResizingRef.current) return;
            const delta = startXRef.current - ev.clientX;
            setNewsfeedWidth(clamp(startWidthRef.current + delta));
        };
        const handleUp = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
            finishResizing();
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
    };

    const handleResizerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isNewsDrawerOpen) return;
        const step = e.shiftKey ? 40 : 10;
        if (e.key === 'ArrowLeft') setNewsfeedWidth(w => clamp(w + step));
        else if (e.key === 'ArrowRight') setNewsfeedWidth(w => clamp(w - step));
    };

    useEffect(() => {
        return () => {
            finishResizing();
        };
    }, []);

    return (
        <div className={classes.container}>
            <ChatList />
            <div className={classes.chatWindowWrapper}>
                {selectedId !== '' && (
                    <ChatWindow
                        onToggleNewsDrawer={toggleNewsDrawer}
                        unreadNewsCount={unreadNewsCount}
                        isNewsDrawerOpen={isNewsDrawerOpen}
                    />
                )}
            </div>
            {isNewsDrawerOpen && (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Größe des Newsfeeds anpassen"
                    aria-valuemin={MIN_NEWSFEED_WIDTH}
                    aria-valuemax={MAX_NEWSFEED_WIDTH}
                    aria-valuenow={newsfeedWidth}
                    tabIndex={0}
                    onKeyDown={handleResizerKeyDown}
                    onPointerDown={startResizing}
                    className={`${classes.resizer} ${isResizingRef.current ? classes.resizerActive : ''}`}
                />
            )}
            <Newsfeed
                open={isNewsDrawerOpen}
                closeable={newsfeedHideable}
                onOpenChange={setIsNewsDrawerOpen}
                onUnreadCountChange={setUnreadNewsCount}
                width={newsfeedWidth}
            />
        </div>
    );
};
